import React, { useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useFieldArray } from "react-hook-form";

import {
  selectWorkOrderIsBuyGet,
  selectWorkOrderResourceType,
} from "../../../../redux/selectors/workOrders";
import usePermissions from "../../../../hooks/usePermissions";
import { BuyGetStatus } from "../../../../types/product/ibrief-product/buy-get-status.enum";
import { isEqual } from "../../../../utils/type.utils";
import { arrayDiff } from "../../../../utils/helpers";
import { withShowIf } from "../../../../components/with-show-if/with-show-If.component";

import { useControlForm } from "../../../forms/forms.hook";

import {
  createBriefProductForm,
  mapProductsToForm,
} from "../../types/brief-product-form/brief-product-form.functions";
import { isBuyGetEnabled } from "../../types/products-table.functions";
import { BriefProductForm } from "../../types/brief-product-form/brief-product-form.interface";

import { ProductsTableView } from "../products-table-view/products-table-view.component";

import { ProductsTableProps } from "./products-table.props";

export const ProductsTable = withShowIf((props: ProductsTableProps) => {
  const permissions = usePermissions();
  const isBuyGet = useSelector(selectWorkOrderIsBuyGet);

  const { setProductsFormIsDirty } = props;
  const {
    control,
    defaultValues,
    errors,
    getValues,
    handleSubmit,
    isDirty,
    setValue,
    trigger,
    updateDefaultValues,
  } = useControlForm({
    defaultValues: mapProductsToForm(props.products),
    mode: "onChange",
    validationRules: props.productsValidation,
    context: { isBuyGet },
    focusOnError: false,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    keyName: "key",
    name: "products",
    shouldUnregister: false,
  });
  const productsRef = useRef(props.products);

  const watchedFields = getValues().products;
  const workOrderGetProductsList: number[] = watchedFields
    .filter((product: BriefProductForm) => Number(product.buyGetStatus) === BuyGetStatus.Get)
    .map((product: BriefProductForm) => Number(product.id));

  useEffect(() => {
    if (props.formRef) {
      props.formRef.current = { errors, handleSubmit, isDirty };
    }

    if (props.setFormErrors) {
      props.setFormErrors(errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, handleSubmit, isDirty]);

  useEffect(() => {
    setProductsFormIsDirty && setProductsFormIsDirty(isDirty);
  }, [isDirty, setProductsFormIsDirty]);

  useEffect(() => {
    if (!isEqual(productsRef.current, props.products)) {
      const newProducts = arrayDiff(props.products, productsRef.current, (product) => product.id);
      newProducts.forEach((product) => append(createBriefProductForm(product)));
      updateDefaultValues(
        {
          products: Object.values({ ...getValues().products, ...defaultValues.products }),
        },
        { keepDirty: false }
      );

      // Trigger warning
      // updateDefaultValues' KeepDirty:true does not actually work for this use-case,
      // so we setValue() with current values to reevaluate dirty-ness
      setValue("products", getValues().products);
      productsRef.current = props.products;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.products]);

  const onBuyGetStatusUpdate = (productId: number) => {
    watchedFields.forEach(async (product: BriefProductForm, index: number) => {
      const fieldName = `products.${index}.relatedGetProductIds`;
      setValue(
        fieldName,
        product.relatedGetProductIds.filter((id) => id !== productId)
      );
    });
  };

  const showBuyGet = useMemo(() => isBuyGetEnabled(isBuyGet, props.pageType), [
    isBuyGet,
    props.pageType,
  ]);

  const onRemoveProduct = async (productIndex: number, productId: number) => {
    onBuyGetStatusUpdate(productId);
    remove(productIndex);
    await trigger();
  };

  return (
    <ProductsTableView
      control={control}
      briefEndDatePassed={props.briefEndDatePassed}
      errors={errors}
      setValue={setValue}
      getValues={getValues}
      triggerValidation={trigger}
      onBuyGetStatusUpdate={onBuyGetStatusUpdate}
      showIf={props.products.length > 0}
      products={props.products}
      fields={fields}
      isBuyGet={isBuyGet}
      disabled={!permissions.manageProducts}
      showBuyGet={showBuyGet}
      workOrderTemplate={useSelector(selectWorkOrderResourceType)}
      workOrderGetProductsList={workOrderGetProductsList}
      pageType={props.pageType}
      onRemoveProduct={onRemoveProduct}
      resourceType={props.resourceType}
    />
  );
});
